/**
 * SearchResultsPage — EXL-669 + EXL-672
 *
 * Route: /search?q=:query&type=all|articles|kb|guides|faqs&page=:n
 *        &category=:cat&tags=:tag&status=:status&dateFrom=:date&dateTo=:date
 *
 * Renders unified full-text search results from the S&S search service with
 * full faceted filtering support:
 *   - SearchFilters sidebar (category, type, tags, status, date range)
 *   - ActiveFilters chip bar showing active filter combinations
 *   - URL-based state: all filters persist in query params for shareability
 *   - BroadcastChannel cache invalidation on search index updates
 *   - Analytics tracking for filter interactions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { SearchFilters, type SearchFacets } from '../components/search/SearchFilters';
import { ActiveFilters } from '../components/search/ActiveFilters';
import { useSearchFilters, parseFiltersFromURL } from '../hooks/useSearchFilters';

// ─── Types ────────────────────────────────────────────────────────────────────

type ContentType = 'all' | 'articles' | 'kb' | 'guides' | 'faqs';

interface SearchHit {
  id: string;
  type: Exclude<ContentType, 'all'>;
  title: string;
  snippet: string;
  url: string;
  score: number;
}

interface SearchResponse {
  results: SearchHit[];
  total: number;
  page: number;
  per_page: number;
  facets: SearchFacets | null;
  warning?: string;
  invalid_params?: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SEARCH_SERVICE_URL =
  typeof window !== 'undefined' &&
  (window as unknown as Record<string, unknown>).__SEARCH_SERVICE_URL__
    ? String((window as unknown as Record<string, unknown>).__SEARCH_SERVICE_URL__)
    : '/api/search';

const PER_PAGE = 20;
const REQUEST_TIMEOUT_MS = 5000;

const TYPE_LABELS: Record<string, string> = {
  articles: 'Article',
  kb: 'Knowledge Base',
  guides: 'Guide',
  faqs: 'FAQ',
};

// ─── Analytics ────────────────────────────────────────────────────────────────

function trackEvent(event: string, props: Record<string, unknown>) {
  // Delegate to analytics integration if available
  if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).__trackEvent__) {
    (window as unknown as Record<string, (...args: unknown[]) => void>).__trackEvent__(event, props);
  }
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function ResultSkeleton() {
  return (
    <div className="animate-pulse space-y-3 border border-neutral-200 rounded-lg p-4">
      <div className="flex items-center gap-2">
        <div className="h-3 bg-neutral-200 rounded w-16" />
        <div className="h-3 bg-neutral-100 rounded w-32" />
      </div>
      <div className="h-5 bg-neutral-200 rounded w-3/4" />
      <div className="space-y-2">
        <div className="h-3 bg-neutral-100 rounded w-full" />
        <div className="h-3 bg-neutral-100 rounded w-11/12" />
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <ResultSkeleton />
      <ResultSkeleton />
      <ResultSkeleton />
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────

function ResultCard({ hit }: { hit: SearchHit }) {
  const typeLabel = TYPE_LABELS[hit.type] ?? hit.type;

  return (
    <article className="border border-neutral-200 rounded-lg p-4 hover:border-neutral-400 hover:shadow-sm transition-all">
      <header className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-coral-600 bg-coral-50 px-2 py-0.5 rounded">
          {typeLabel}
        </span>
      </header>
      <h2 className="text-base font-semibold text-neutral-900 mb-1.5">
        <Link
          to={hit.url}
          className="hover:text-coral-600 transition-colors"
        >
          {hit.title}
        </Link>
      </h2>
      {hit.snippet && (
        <p
          className="text-sm text-neutral-600 leading-relaxed"
          /* snippet may contain <mark> HTML from server */
          dangerouslySetInnerHTML={{ __html: hit.snippet }}
        />
      )}
    </article>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

interface PaginationProps {
  page: number;
  total: number;
  perPage: number;
  onPageChange: (p: number) => void;
}

function Pagination({ page, total, perPage, onPageChange }: PaginationProps) {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;

  return (
    <nav className="flex items-center justify-center gap-2 mt-8" aria-label="Search result pages">
      <button
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        className="px-3 py-1.5 text-sm rounded border border-neutral-300 disabled:opacity-40
          hover:bg-neutral-50 transition-colors"
      >
        Previous
      </button>
      <span className="text-sm text-neutral-600">
        Page {page} of {totalPages}
      </span>
      <button
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        className="px-3 py-1.5 text-sm rounded border border-neutral-300 disabled:opacity-40
          hover:bg-neutral-50 transition-colors"
      >
        Next
      </button>
    </nav>
  );
}

// ─── Error / empty states ─────────────────────────────────────────────────────

interface EmptyStateProps {
  query: string;
  hasFilters: boolean;
  onClearFilters: () => void;
}

function EmptyState({ query, hasFilters, onClearFilters }: EmptyStateProps) {
  if (hasFilters) {
    return (
      <div className="text-center py-16">
        <p className="text-neutral-500 text-base mb-4">
          No results match your filters. Try removing some filters.
        </p>
        <button
          onClick={onClearFilters}
          className="px-4 py-2 bg-neutral-900 text-white rounded-lg text-sm font-medium
            hover:bg-neutral-700 transition-colors"
        >
          Clear all filters
        </button>
      </div>
    );
  }
  return (
    <div className="text-center py-16">
      <p className="text-neutral-500 text-base">
        No results found for <strong className="text-neutral-800">&ldquo;{query}&rdquo;</strong>.
      </p>
      <p className="text-sm text-neutral-400 mt-2">
        Try different keywords or broaden your search.
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-neutral-500 text-base mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-neutral-900 text-white rounded-lg text-sm font-medium
          hover:bg-neutral-700 transition-colors"
      >
        Try again
      </button>
    </div>
  );
}

// ─── Search bar ───────────────────────────────────────────────────────────────

function SearchBar({ defaultValue, onSubmit }: { defaultValue: string; onSubmit: (q: string) => void }) {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) onSubmit(value.trim());
      }}
      className="flex gap-2"
    >
      <input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search articles, guides, FAQs…"
        className="flex-1 px-4 py-2.5 border border-neutral-300 rounded-lg text-sm
          focus:outline-none focus:ring-1 focus:ring-neutral-900/20 focus:border-neutral-500"
        aria-label="Search query"
      />
      <button
        type="submit"
        className="px-5 py-2.5 bg-neutral-900 text-white rounded-lg text-sm font-medium
          hover:bg-neutral-700 transition-colors"
      >
        Search
      </button>
    </form>
  );
}

// ─── Toast notification ───────────────────────────────────────────────────────

interface Toast {
  id: number;
  message: string;
  type: 'error' | 'warning';
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 flex flex-col gap-2 z-50" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm text-white
            ${t.type === 'error' ? 'bg-red-600' : 'bg-amber-600'}`}
          role="alert"
        >
          <span>{t.message}</span>
          <button
            onClick={() => onDismiss(t.id)}
            className="flex-shrink-0 hover:opacity-80 transition-opacity"
            aria-label="Dismiss notification"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = searchParams.get('q') ?? '';
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);

  const [data, setData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);
  const lastQueryRef = useRef<string>('');
  const firstResultRef = useRef<HTMLDivElement | null>(null);

  // ── Filter state from URL ─────────────────────────────────────────────────

  const filters = useSearchFilters();
  const { hasActiveFilters, activeFilterCount } = filters;

  const showToast = useCallback((message: string, type: Toast['type'] = 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Search execution ──────────────────────────────────────────────────────

  const performSearch = useCallback(
    async (q: string, p: number, forceFilters?: typeof filters) => {
      if (!q.trim()) return;

      const activeFilters = forceFilters ?? filters;
      setLoading(true);
      setError(null);

      // Build URL params including all filter params
      const params = new URLSearchParams({ q, page: String(p), per_page: String(PER_PAGE) });
      if (activeFilters.type) params.set('type', activeFilters.type);
      for (const c of activeFilters.categories) params.append('category', c);
      for (const t of activeFilters.tags) params.append('tags', t);
      for (const s of activeFilters.statuses) params.append('status', s);
      if (activeFilters.dateFrom) params.set('dateFrom', activeFilters.dateFrom);
      if (activeFilters.dateTo) params.set('dateTo', activeFilters.dateTo);

      // Request timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, REQUEST_TIMEOUT_MS + 1000);

      try {
        const res = await fetch(`${SEARCH_SERVICE_URL}?${params}`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (res.status === 503) {
          setError('Search is temporarily unavailable. Please try again in a moment.');
          // Keep existing data / facets visible
          return;
        }

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setError(body.error ?? 'Search request failed.');
          setData(null);
          return;
        }

        const json = (await res.json()) as SearchResponse;
        setData(json);

        // Warn about stripped invalid params
        if (json.warning) {
          showToast('Some filters were invalid and removed.', 'warning');
        }

        // Track zero-result filter states
        if (json.total === 0 && hasActiveFilters) {
          trackEvent('zero_results_filters', {
            q,
            categories: activeFilters.categories,
            type: activeFilters.type,
            tags: activeFilters.tags,
            statuses: activeFilters.statuses,
            dateFrom: activeFilters.dateFrom,
            dateTo: activeFilters.dateTo,
          });
        }

        // Move focus to first result after filter application
        requestAnimationFrame(() => {
          firstResultRef.current?.focus();
        });
      } catch (err: unknown) {
        clearTimeout(timeoutId);
        const e = err as Error;
        if (e.name === 'AbortError') {
          setError('Search request timed out. Please try again.');
          showToast('Unable to load filters. Please try again.');
        } else {
          setError('Unable to connect to the search service. Please try again.');
        }
        // Keep existing data visible (per spec: keep results visible on failure)
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, hasActiveFilters, showToast]
  );

  // Trigger search on URL param changes
  useEffect(() => {
    if (query) {
      const currentFilters = parseFiltersFromURL(searchParams);
      void performSearch(query, page, {
        categories: currentFilters.categories,
        type: currentFilters.type,
        tags: currentFilters.tags,
        statuses: currentFilters.statuses,
        dateFrom: currentFilters.dateFrom,
        dateTo: currentFilters.dateTo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page, searchParams.toString()]);

  // BroadcastChannel: invalidate React Query-like cache on index update
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel('search_index_updated');
    const handler = () => {
      if (query) void performSearch(query, page);
    };
    bc.addEventListener('message', handler);
    return () => {
      bc.removeEventListener('message', handler);
      bc.close();
    };
  }, [query, page, performSearch]);

  // ── URL state helpers ────────────────────────────────────────────────────

  const updateSearch = (updates: Partial<{ q: string; page: number }>) => {
    const next = new URLSearchParams(searchParams);
    if (updates.q !== undefined) next.set('q', updates.q);
    if (updates.page !== undefined) {
      if (updates.page === 1) next.delete('page');
      else next.set('page', String(updates.page));
    }
    setSearchParams(next, { replace: false });
  };

  const handleNewQuery = (q: string) => {
    // Clear all filters on new query
    navigate(`/search?q=${encodeURIComponent(q)}`, { replace: false });
    lastQueryRef.current = q;
  };

  const handlePageChange = (p: number) => {
    updateSearch({ page: p });
  };

  // ── Filter action handlers ───────────────────────────────────────────────

  const handleTypeChange = (value: string) => {
    filters.setType(value);
    trackEvent('filter_applied', { facet: 'type', value, result_count: data?.total ?? 0 });
  };

  const handleCategoryRemove = (value: string) => {
    const next = filters.categories.filter((c) => c !== value);
    filters.setCategories(next);
    trackEvent('filter_removed', { facet: 'category', value });
  };

  const handleTypeRemove = () => {
    filters.setType('');
    trackEvent('filter_removed', { facet: 'type', value: filters.type });
  };

  const handleTagRemove = (value: string) => {
    const next = filters.tags.filter((t) => t !== value);
    filters.setTags(next);
    trackEvent('filter_removed', { facet: 'tags', value });
  };

  const handleStatusRemove = (value: string) => {
    const next = filters.statuses.filter((s) => s !== value);
    filters.setStatuses(next);
    trackEvent('filter_removed', { facet: 'status', value });
  };

  const handleDateRangeRemove = () => {
    filters.setDateRange('', '');
    trackEvent('filter_removed', { facet: 'date_range', value: `${filters.dateFrom}..${filters.dateTo}` });
  };

  const handleClearAll = () => {
    const removedCount = activeFilterCount;
    filters.clearAll();
    trackEvent('filters_cleared', { count: removedCount });
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const showResults = !loading && !error && data && data.results.length > 0;
  const showEmpty = !loading && !error && data && data.results.length === 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-3">
            <Link to="/" className="text-sm font-medium text-neutral-500 hover:text-neutral-800 transition-colors">
              Home
            </Link>
            <span className="text-neutral-300">/</span>
            <span className="text-sm text-neutral-700">Search</span>
          </div>
          <SearchBar defaultValue={query} onSubmit={handleNewQuery} />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Active filters chip bar */}
        {query && hasActiveFilters && (
          <div className="mb-4">
            <ActiveFilters
              filters={filters}
              onCategoryRemove={handleCategoryRemove}
              onTypeRemove={handleTypeRemove}
              onTagRemove={handleTagRemove}
              onStatusRemove={handleStatusRemove}
              onDateRangeRemove={handleDateRangeRemove}
              onClearAll={handleClearAll}
            />
          </div>
        )}

        <div className="flex gap-6">
          {/* Sidebar filters */}
          {query && (
            <aside className="hidden lg:block w-56 flex-shrink-0">
              <SearchFilters
                facets={data?.facets ?? null}
                filters={filters}
                loading={loading}
                onCategoriesChange={(vals) => {
                  filters.setCategories(vals);
                  for (const v of vals) trackEvent('filter_applied', { facet: 'category', value: v, result_count: data?.total ?? 0 });
                }}
                onTypeChange={handleTypeChange}
                onTagsChange={(vals) => {
                  filters.setTags(vals);
                  for (const v of vals) trackEvent('filter_applied', { facet: 'tags', value: v, result_count: data?.total ?? 0 });
                }}
                onStatusesChange={(vals) => {
                  filters.setStatuses(vals);
                  for (const v of vals) trackEvent('filter_applied', { facet: 'status', value: v, result_count: data?.total ?? 0 });
                }}
                onDateRangeChange={(from, to) => {
                  filters.setDateRange(from, to);
                  if (from || to) trackEvent('filter_applied', { facet: 'date_range', value: `${from}..${to}`, result_count: data?.total ?? 0 });
                }}
                onClearAll={handleClearAll}
                hasActiveFilters={hasActiveFilters}
              />
            </aside>
          )}

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Result summary */}
            {!loading && data && data.total > 0 && (
              <p className="text-sm text-neutral-500 mb-4">
                {data.total.toLocaleString()} result{data.total !== 1 ? 's' : ''} for{' '}
                <strong className="text-neutral-800">&ldquo;{query}&rdquo;</strong>
                {hasActiveFilters && (
                  <span className="ml-1 text-neutral-400">
                    ({activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active)
                  </span>
                )}
              </p>
            )}

            {/* Loading skeleton */}
            {loading && <LoadingSkeleton />}

            {/* Error state */}
            {error && (
              <ErrorState
                message={error}
                onRetry={() => void performSearch(query, page)}
              />
            )}

            {/* Empty state */}
            {showEmpty && (
              <EmptyState
                query={query}
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearAll}
              />
            )}

            {/* Results */}
            {showResults && (
              <div className="space-y-4" ref={firstResultRef} tabIndex={-1} aria-live="polite">
                {data.results.map((hit) => (
                  <ResultCard key={`${hit.type}:${hit.id}`} hit={hit} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {showResults && (
              <Pagination
                page={page}
                total={data.total}
                perPage={PER_PAGE}
                onPageChange={handlePageChange}
              />
            )}

            {/* No query state */}
            {!query && !loading && (
              <div className="text-center py-16 text-neutral-400 text-sm">
                Enter a search query above to get started.
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

export default SearchResultsPage;
